import React from 'react';
import { Network, Brain, Heart, Wind, Eye, Scan, Utensils, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../../components/ui/badge';

const HealthNet = () => {
  const navigate = useNavigate();

  const modules = [
    { name: 'Cardio', icon: Heart, color: '#14B8A6', status: 'In Development' },
    { name: 'Neuro', icon: Brain, color: '#7C3AED', status: 'Planned' },
    { name: 'Derma', icon: Scan, color: '#EC4899', status: 'Planned' },
    { name: 'Pulmo', icon: Wind, color: '#3B82F6', status: 'Planned' },
    { name: 'Vision', icon: Eye, color: '#8B5CF6', status: 'Planned' },
    { name: 'NutriScan', icon: Utensils, color: '#10B981', status: 'Planned' },
  ];

  const integrationFeatures = [
    {
      title: 'Unified Patient Profile',
      description: 'A comprehensive health dashboard aggregating insights from all organ-based AI modules.',
    },
    {
      title: 'Cross-System Correlation',
      description: 'Identifying patterns and connections between different organ systems for holistic diagnostics.',
    },
    {
      title: 'Longitudinal Health Tracking',
      description: 'Monitoring patient health evolution over time across multiple modalities.',
    },
    {
      title: 'Predictive Health Analytics',
      description: 'Using combined data to predict future health risks and recommend preventive measures.',
    },
  ];

  return (
    <div className="min-h-screen bg-[#0B1220]">
      {/* Hero Banner */}
      <div className="relative bg-gradient-to-r from-[#F59E0B]/20 via-[#0B1220] to-[#7C3AED]/20 border-b border-[#1F2A3A] pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-xl bg-[#F59E0B]/20 flex items-center justify-center">
              <Network size={32} className="text-[#F59E0B]" />
            </div>
            <div>
              <Badge className="bg-[#F59E0B]/20 text-[#F59E0B] border-[#F59E0B]/30 mb-2">
                Vision 2035
              </Badge>
              <h1 className="text-5xl md:text-6xl font-bold text-white">
                Yamai HealthNet
              </h1>
            </div>
          </div>
          <p className="text-[#8A90A0] text-xl max-w-3xl">
            A Unified Diagnostic Ecosystem Integrating Multiple Organ-Based AI Models
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Vision Statement */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-6">The Vision</h2>
          <div className="bg-gradient-to-br from-[#F59E0B]/10 to-[#7C3AED]/10 backdrop-blur-sm rounded-2xl p-8 border border-[#F59E0B]/30">
            <p className="text-[#E6E8EF] text-lg leading-relaxed mb-4">
              By 2035, Yamai HealthNet will transform how we approach medical diagnostics by creating the
              world's first fully integrated, explainable AI health intelligence platform. Rather than viewing
              organs in isolation, HealthNet recognizes that the human body is an interconnected system where
              changes in one area often signal or influence conditions elsewhere.
            </p>
            <p className="text-[#8A90A0] leading-relaxed">
              HealthNet will unify our specialized AI modules — Cardio, Neuro, Derma, Pulmo, Vision, and
              NutriScan — into a single, holistic diagnostic assistant that empowers physicians with
              unprecedented insight into patient health across all major organ systems.
            </p>
          </div>
        </section>

        {/* Why HealthNet Matters */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-6">Why It Matters</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-[#1F2A3A]/30 backdrop-blur-sm rounded-xl p-6 border border-[#1F2A3A]">
              <h3 className="text-xl font-semibold text-white mb-3">Holistic Health Understanding</h3>
              <p className="text-[#8A90A0] text-sm leading-relaxed">
                Many diseases manifest symptoms across multiple organ systems. For example, cardiovascular
                issues can affect kidney function, while metabolic disorders impact heart health. HealthNet
                identifies these connections.
              </p>
            </div>
            <div className="bg-[#1F2A3A]/30 backdrop-blur-sm rounded-xl p-6 border border-[#1F2A3A]">
              <h3 className="text-xl font-semibold text-white mb-3">Earlier Disease Detection</h3>
              <p className="text-[#8A90A0] text-sm leading-relaxed">
                By analyzing patterns across multiple data sources simultaneously, HealthNet can detect
                subtle warning signs that might be missed when evaluating systems in isolation.
              </p>
            </div>
            <div className="bg-[#1F2A3A]/30 backdrop-blur-sm rounded-xl p-6 border border-[#1F2A3A]">
              <h3 className="text-xl font-semibold text-white mb-3">Reduced Healthcare Costs</h3>
              <p className="text-[#8A90A0] text-sm leading-relaxed">
                Preventive care and early intervention are significantly more cost-effective than treating
                advanced diseases. HealthNet prioritizes prevention through comprehensive monitoring.
              </p>
            </div>
            <div className="bg-[#1F2A3A]/30 backdrop-blur-sm rounded-xl p-6 border border-[#1F2A3A]">
              <h3 className="text-xl font-semibold text-white mb-3">Personalized Medicine</h3>
              <p className="text-[#8A90A0] text-sm leading-relaxed">
                Every patient is unique. HealthNet builds comprehensive health profiles that enable
                truly personalized treatment recommendations based on multi-organ correlations.
              </p>
            </div>
          </div>
        </section>

        {/* Integrated Modules */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-6">Integrated AI Modules</h2>
          <p className="text-[#8A90A0] mb-8 max-w-3xl">
            HealthNet will bring together six specialized diagnostic modules, each an expert in its domain:
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {modules.map((module, index) => (
              <div
                key={index}
                className="bg-[#1F2A3A]/30 backdrop-blur-sm rounded-xl p-6 border border-[#1F2A3A] hover:border-[#F59E0B]/50 transition-all duration-300"
              >
                <div
                  className="w-12 h-12 rounded-lg mb-4 flex items-center justify-center"
                  style={{ backgroundColor: `${module.color}33` }}
                >
                  <module.icon size={24} style={{ color: module.color }} />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Yamai {module.name}</h3>
                <Badge
                  className={`${
                    module.status === 'In Development'
                      ? 'bg-[#14B8A6]/20 text-[#14B8A6] border-[#14B8A6]/30'
                      : 'bg-[#8A90A0]/20 text-[#8A90A0] border-[#8A90A0]/30'
                  }`}
                >
                  {module.status}
                </Badge>
              </div>
            ))}
          </div>
        </section>

        {/* How It Works */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-6">How HealthNet Integration Works</h2>
          <div className="space-y-6">
            <div className="bg-[#1F2A3A]/30 backdrop-blur-sm rounded-xl p-6 border border-[#1F2A3A]">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#7C3AED]/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#7C3AED] font-bold">1</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">Data Collection & Processing</h3>
                  <p className="text-[#8A90A0] leading-relaxed">
                    Each specialized module processes its respective data type (echocardiograms, EEG, retinal
                    scans, etc.) and generates diagnostic insights and confidence scores.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-[#1F2A3A]/30 backdrop-blur-sm rounded-xl p-6 border border-[#1F2A3A]">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#14B8A6]/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#14B8A6] font-bold">2</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">Cross-System Fusion</h3>
                  <p className="text-[#8A90A0] leading-relaxed">
                    A central fusion layer analyzes outputs from all modules simultaneously, identifying
                    correlations and patterns that span multiple organ systems.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-[#1F2A3A]/30 backdrop-blur-sm rounded-xl p-6 border border-[#1F2A3A]">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#F59E0B]/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#F59E0B] font-bold">3</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">Explainable Insights Generation</h3>
                  <p className="text-[#8A90A0] leading-relaxed">
                    Results are presented through an intuitive dashboard showing not just what was found,
                    but why — with clear explanations of inter-system connections and risk factors.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-[#1F2A3A]/30 backdrop-blur-sm rounded-xl p-6 border border-[#1F2A3A]">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#10B981]/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#10B981] font-bold">4</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">Clinical Decision Support</h3>
                  <p className="text-[#8A90A0] leading-relaxed">
                    Physicians receive actionable recommendations with supporting evidence from multiple
                    data sources, enabling more informed treatment decisions.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-6">Core Integration Features</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {integrationFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-[#F59E0B]/10 to-[#7C3AED]/10 backdrop-blur-sm rounded-xl p-6 border border-[#F59E0B]/30"
              >
                <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
                <p className="text-[#8A90A0] leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Roadmap */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-6">Path to 2035</h2>
          <div className="bg-[#1F2A3A]/30 backdrop-blur-sm rounded-2xl p-8 border border-[#1F2A3A]">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">2025-2028: Foundation Phase</h3>
                <p className="text-[#8A90A0] text-sm">
                  Develop and validate individual organ-specific modules (starting with Cardio)
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">2029-2031: Integration Phase</h3>
                <p className="text-[#8A90A0] text-sm">
                  Build the central fusion platform and begin two-module integration testing
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">2032-2034: Expansion Phase</h3>
                <p className="text-[#8A90A0] text-sm">
                  Complete full six-module integration with comprehensive clinical validation
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">2035: Launch</h3>
                <p className="text-[#8A90A0] text-sm">
                  Commercial release of Yamai HealthNet across European healthcare systems
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center">
          <div className="bg-gradient-to-r from-[#F59E0B]/20 via-[#0B1220] to-[#7C3AED]/20 rounded-2xl p-12 border border-[#F59E0B]/30">
            <h2 className="text-3xl font-bold text-white mb-4">Be Part of the Future</h2>
            <p className="text-[#8A90A0] mb-8 max-w-2xl mx-auto">
              We're building partnerships with visionary healthcare organizations to make HealthNet a reality.
            </p>
            <Button
              size="lg"
              onClick={() => navigate('/contact')}
              className="bg-gradient-to-r from-[#F59E0B] to-[#7C3AED] hover:opacity-90 text-white px-8 py-6 rounded-xl"
            >
              Partner With Us
              <ArrowRight size={20} className="ml-2" />
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default HealthNet;